import { create } from 'zustand'
import { NotificationItem } from '@/lib/models/NotificationItem'
import { ModalContent } from '@/shared/containers/ModalContainer'
import { TwilioWidgetState } from '@/features/twilio/TwilioModels'
import { Call, Device } from '@twilio/voice-sdk'
import { toast } from 'sonner'
import { captureTwilioWidgetError } from '@/lib/twilio-widget-errors'

export type Position = { x: number; y: number }

export type MediaDevice = {
  type: 'audioinput' | 'audiooutput'
  deviceId: string
  groupId: string
  name: string
  default: boolean
}

export type WrappedCall =
  | {
      call: Call
      startTime: number
      muted: boolean
      status: 'CONNECTED'
    }
  | { status: 'CONNECTING' }

export type TwilioContext = {
  audioContext: AudioContext
  device: Device
  call: WrappedCall | null

  availableInput: MediaDevice[]
  selectedInput: MediaDevice
  availableOutput: MediaDevice[]

  // On safari, enumerate devices will not return any 'audiooutput' devices. In essence, it's not possible to select
  // the output device on safari so if it's null, I know I can't change the output device and act accordingly.
  selectedOutput: MediaDevice | null
}

interface GlobalState {
  loading: boolean
  notifications: NotificationItem[]
  showNotification: (item: NotificationItem) => void
  clearNotification: (id: string) => void

  modal: ModalContent | null
  showModal: (content: ModalContent) => void
  closeModal: () => void

  mutatingItems: Set<string>
  startMutatingItem: (reference: string) => void
  endMutatingItem: (reference: string) => void

  certificateListPage: number
  setCertificateListPage: (page: number) => void

  twilioWidgetState: TwilioWidgetState
  setTwilioState: (state: TwilioWidgetState) => void

  // Need to persist
  twilioWidgetPosition: Position | null
  setTwilioWidgetPosition: (position: Position) => void

  // Need to persist
  twilioContext: TwilioContext | null
  setTwilioContext: (state: TwilioContext) => void
  completeTwilioSetup: (
    audioContext: AudioContext,
    device: Device,
    availableInput: MediaDevice[],
    selectedInput: MediaDevice,
    availableOutput: MediaDevice[],
    selectedOutput: MediaDevice | null,
  ) => void
  changeOutputDevice: (deviceId: string) => void
  changeInputDevice: (deviceId: string) => void
  setTwilioCallState: (call: WrappedCall | null) => void
  setLoading: (loading: boolean) => void
}

export const useStore = create<GlobalState>((set) => ({
  loading: false,
  notifications: [],
  showNotification: (item) => {
    switch (item.variant) {
      case 'warning':
        toast.warning(item.title)
        return
      case 'error':
        toast.error(item.title)
        return
      case 'success':
        toast.success(item.title)
        return
      case 'info':
        toast.info(item.title, { richColors: false })
        return
    }
    set((state) => ({ notifications: [...state.notifications, item] }))
  },
  clearNotification: (id: string) => {
    set((state) => ({
      notifications: state.notifications.filter((item) => item.id !== id),
    }))
  },

  modal: null,
  showModal: (content) => {
    set(() => ({ modal: content }))
  },
  closeModal: () => {
    set(() => ({ modal: null }))
  },

  mutatingItems: new Set(),
  startMutatingItem: (reference: string) => {
    set((state) => ({
      mutatingItems: new Set(state.mutatingItems).add(reference),
    }))
  },
  endMutatingItem: (reference: string) => {
    set((state) => {
      const next = new Set(state.mutatingItems)
      next.delete(reference)
      return { mutatingItems: next }
    })
  },

  certificateListPage: 0,
  setCertificateListPage: (page: number) => {
    set(() => ({ certificateListPage: page }))
  },

  twilioWidgetState: { state: 'HIDDEN' },
  setTwilioState: (state: TwilioWidgetState) => {
    set(() => ({ twilioWidgetState: state }))
  },

  twilioWidgetPosition: null,
  setTwilioWidgetPosition: (position) => {
    set(() => ({ twilioWidgetPosition: position }))
  },

  twilioContext: null,
  setTwilioContext: (ioState) => {
    set(() => ({ twilioContext: ioState }))
  },
  completeTwilioSetup: (
    audioContext: AudioContext,
    device: Device,
    availableInput: MediaDevice[],
    selectedInput: MediaDevice,
    availableOutput: MediaDevice[],
    selectedOutput: MediaDevice | null,
  ) => {
    set(() => ({
      twilioContext: {
        audioContext,
        device,
        call: null,
        availableInput,
        selectedInput,
        availableOutput,
        selectedOutput,
      },
    }))
  },
  changeOutputDevice: (deviceId: string) => {
    set((state) => {
      const context = state.twilioContext
      if (!context) {
        captureTwilioWidgetError(
          new Error('Twilio context missing while changing output device'),
          {
            error_source: 'output_device_change_failed',
            has_active_call: false,
          },
        )
        return state
      }

      const selectedOutput = context.availableOutput.find(
        (device) => device.deviceId === deviceId,
      )
      if (!selectedOutput) {
        captureTwilioWidgetError(
          new Error('Selected Twilio output device not found'),
          {
            error_source: 'output_device_change_failed',
            device_state: context.device.state,
            has_active_call: !!context.call,
          },
        )
        return state
      }

      void context.device.audio?.speakerDevices
        .set(selectedOutput.deviceId)
        .catch((error) =>
          captureTwilioWidgetError(error, {
            error_source: 'output_device_change_failed',
            device_state: context.device.state,
            has_active_call: !!context.call,
          }),
        )

      return {
        twilioContext: {
          ...context,
          selectedOutput,
        },
      }
    })
  },
  changeInputDevice: (deviceId: string) => {
    set((state) => {
      const context = state.twilioContext
      if (!context) {
        captureTwilioWidgetError(
          new Error('Twilio context missing while changing input device'),
          {
            error_source: 'input_device_change_failed',
            has_active_call: false,
          },
        )
        return state
      }

      const selectedInput = context.availableInput.find(
        (device) => device.deviceId === deviceId,
      )
      if (!selectedInput) {
        captureTwilioWidgetError(
          new Error('Selected Twilio input device not found'),
          {
            error_source: 'input_device_change_failed',
            device_state: context.device.state,
            has_active_call: !!context.call,
          },
        )
        return state
      }

      void context.device.audio
        ?.setInputDevice(selectedInput.deviceId)
        .catch((error) =>
          captureTwilioWidgetError(error, {
            error_source: 'input_device_change_failed',
            device_state: context.device.state,
            has_active_call: !!context.call,
          }),
        )

      return {
        twilioContext: {
          ...context,
          selectedInput,
        },
      }
    })
  },
  setTwilioCallState: (call: WrappedCall | null) => {
    set((state) => {
      if (!state.twilioContext) return state

      return {
        twilioContext: {
          ...state.twilioContext,
          call,
        },
      }
    })
  },
  setLoading: (loading: boolean) => {
    set((state) => ({
      ...state,
      loading,
    }))
  },
}))
